import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import usePlans from "../../hooks/usePlans";
import { useToast } from "../../hooks/useToast";
import { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";
import "./style.css";
import ColorModeContext from "../../layout/themeContext";
import { Helmet } from "react-helmet-async";
import { Moon, Sun } from "lucide-react";
import LoginPhoneInput from "../../components/LoginPhoneInput";
import { isValidContactPhone } from "../../helpers/contactPhoneValidation";


const hexToRgb = (hex) => {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const Login = () => {
  const history = useHistory();
  const isMounted = useRef(true);
  const cardRef = useRef(null);

  const [user, setUser] = useState(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    return { email: savedEmail || "", password: "" };
  });
  const [rememberMe, setRememberMe] = useState(() => {
    return !!localStorage.getItem("rememberedEmail");
  });
  const [signupData, setSignupData] = useState({
    companyName: "",
    name: "",
    email: "",
    password: "",
    phone: "",
    planId: "",
  });
  const [allowSignup, setAllowSignup] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nonce] = useState(() => Math.random().toString(36).slice(2));
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [blobsLoaded, setBlobsLoaded] = useState(false);

  useEffect(() => {
    // Atraso intencional para os monstrinhos caírem após a tela carregar
    const timer = setTimeout(() => {
      if (isMounted.current) {
        setBlobsLoaded(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const { getPublicSetting } = useSettings();
  const { handleLogin } = useContext(AuthContext);
  const { getPlanList } = usePlans();
  const toast = useToast();
  const [plans, setPlans] = useState([]);

  const colorModeContext = useContext(ColorModeContext);
  const { mode, appName, appLogoFavicon } = colorModeContext;

  const primaryColorLight = colorModeContext.primaryColorLight;
  const primaryColorDark = colorModeContext.primaryColorDark;
  const loginBackgroundColor = colorModeContext.loginBackgroundColor;

  const handleToggleMode = () => {
    colorModeContext.toggleColorMode();
  };

  const handleChangeInput = (e) => {
    setError("");
    const { name, value } = e.target;
    setUser({ ...user, [name]: value });
  };

  const handleSignupChange = (e) => {
    setError("");
    const { name, value } = e.target;
    if (name.startsWith("fullName")) {
      setSignupData({ ...signupData, name: value });
      return;
    }
    if (name.startsWith("signupPwd")) {
      setSignupData({ ...signupData, password: value });
      return;
    }
    setSignupData({ ...signupData, [name]: value });
  };

  const handleSubmitLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (rememberMe) {
      localStorage.setItem("rememberedEmail", user.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    if (cardRef.current) {
        cardRef.current.classList.add("loading-active");
    }

    try {
      await handleLogin(user);
    } catch (err) {
      if (cardRef.current) {
          cardRef.current.classList.remove("loading-active");
      }
      const errorCode = err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "ERR_UNKNOWN";

      const translationKey = `backendErrors.${errorCode}`;
      const translatedError = i18n.exists(translationKey)
        ? i18n.t(translationKey)
        : errorCode === "ERR_INVALID_CREDENTIALS"
          ? "Usuário ou senha inválidos"
          : "Erro ao fazer login. Tente novamente.";

      if (isMounted.current) {
        setError(translatedError);
        if (cardRef.current) {
          cardRef.current.classList.add("error-active");
          setTimeout(() => {
            if (cardRef.current) cardRef.current.classList.remove("error-active");
          }, 500);
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!isMounted.current) return;

    setError("");
    setLoading(true);

    if (cardRef.current) {
        cardRef.current.classList.add("loading-active");
    }

    try {
      if (!isValidContactPhone(signupData.phone)) {
        if (isMounted.current) {
          setError(i18n.t("contactModal.error.numberInvalid"));
          setLoading(false);
          if (cardRef.current) cardRef.current.classList.remove("loading-active");
        }
        return;
      }

      await openApi.post("/auth/signup", { ...signupData });

      if (isMounted.current) {
        toast.success(i18n.t("signup.toasts.success"));
        setIsSignupMode(false);
        if (cardRef.current) cardRef.current.classList.remove("loading-active");
        setSignupData({
          companyName: "",
          name: "",
          email: "",
          password: "",
          phone: "",
          planId: "",
        });
      }
    } catch (err) {
      if (isMounted.current) {
        if (cardRef.current) cardRef.current.classList.remove("loading-active");
        setError(err.response?.data?.error || "Erro ao criar conta");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getPublicSetting("userCreation")
      .then((data) => {
        if (isMounted.current) {
          setAllowSignup(data === "enabled");
        }
      })
      .catch(() => { });
  }, [getPublicSetting]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const planList = await getPlanList({ listPublic: "false" });
        if (isMounted.current) {
          setPlans(planList);
        }
      } catch (error) {
        console.error("Erro ao buscar planos:", error);
      }
    };
    fetchData();
  }, [getPlanList]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    document.body.classList.add("login-page-body");
    if (mode === "dark") {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }

    return () => {
      document.body.classList.remove("login-page-body");
      document.body.classList.remove("dark");
    };
  }, [mode]);

  useEffect(() => {
    const currentPrimaryColor = mode === "light" ? primaryColorLight : primaryColorDark;
    const primaryRgb = hexToRgb(currentPrimaryColor);
    const root = document.documentElement;

    root.style.setProperty("--primaryColor", currentPrimaryColor);
    root.style.setProperty("--primary-rgb", primaryRgb);

    // Keep this logic for global styles to not break anything
    const defaultBgLight = "#eae6df";
    const defaultBgDark = "#0b141a";
    const bgColor = mode === "light"
      ? (loginBackgroundColor || defaultBgLight)
      : defaultBgDark;
    root.style.setProperty("--theme-background-default", bgColor);

    // Apply the configured background to the new theme variable as well
    if (loginBackgroundColor && mode === "light") {
      root.style.setProperty("--new-theme-bg", loginBackgroundColor);
    } else if (mode === "light") {
      root.style.setProperty("--new-theme-bg", "#dedad4"); // New default layout bg
    } else {
      root.style.setProperty("--new-theme-bg", "#0b141a");
    }

    if (mode === "light") {
      root.style.setProperty("--theme-background-paper", "#ffffff");
      root.style.setProperty("--theme-text-primary", "#111b21");
      root.style.setProperty("--theme-text-secondary", "#667781");
      root.style.setProperty("--theme-text-disabled", "#a0adb5");
      root.style.setProperty("--theme-border-light", "#e9edef");
      root.style.setProperty("--theme-border-medium", "#d1d7db");
    } else {
      root.style.setProperty("--theme-background-paper", "#111b21");
      root.style.setProperty("--theme-text-primary", "#e9edef");
      root.style.setProperty("--theme-text-secondary", "#8696a0");
      root.style.setProperty("--theme-text-disabled", "#54656f");
      root.style.setProperty("--theme-border-light", "#222d34");
      root.style.setProperty("--theme-border-medium", "#2a3942");
    }
  }, [mode, primaryColorLight, primaryColorDark, loginBackgroundColor]);

  // Eye mouse tracking logic
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!cardRef.current) return;
      const passwordInput = document.getElementById("password");
      const signupPasswordInput = document.getElementById(`signupPwd_${nonce}`);
      
      if (
        document.activeElement === passwordInput || 
        document.activeElement === signupPasswordInput || 
        cardRef.current.classList.contains("error-active")
      ) {
        return;
      }

      // FÁCIL: Com a inserção do EyeBox Branco em tudo como conteiner absoluto os cortes se mantem com a forma do "Box / Border 50%". Nenhum hack matemático ou esfrega mais. A pupila limita perfeitamente ao globo branco na qual mora de forma universal : 

      const pupils = document.querySelectorAll(".pupil");
      pupils.forEach((pupil) => {
        const eye = pupil.parentElement;
        const rect = eye.getBoundingClientRect();

        const eyeCenterX = rect.left + rect.width / 2;
        const eyeCenterY = rect.top + rect.height / 2;
        const angle = Math.atan2(e.clientY - eyeCenterY, e.clientX - eyeCenterX);

        // Exatamente como fisíca de contenção : Radius do globo do CSS dividdo pelo Radius pupilar dele , descontado em padding p ficar suave: 
        const eyeRadius = rect.width / 2;
        const pupilRadius = pupil.getBoundingClientRect().width / 2;
        
        // A mágica simples, nunca sai da esclera branca base
        const maxDistance = eyeRadius - pupilRadius - 0.5;
        
        const rawDistance = Math.hypot(e.clientX - eyeCenterX, e.clientY - eyeCenterY) / 35; // a força que reage
        const distance = Math.min(maxDistance, rawDistance); // Breca na bordinha exato

        const moveX = Math.cos(angle) * distance;
        let moveY = Math.sin(angle) * distance;

        pupil.style.transform = `translate(${moveX}px, ${moveY}px)`;

        // Pato Amarelo: Bico acompanha o movimento dos olhos (exagerado levemente para dar efeito 3D)
        if (pupil.closest('.blob-yellow')) {
            const yellowBeak = document.querySelector('.yellow-mouth');
            if (yellowBeak) {
                yellowBeak.style.transform = `translate(${moveX * 1.8}px, ${moveY * 1.8}px) rotate(8deg)`;
            }
        }
      });

      // PARALLAX BOQUINHAS QUE RESPONDEM AÇÃO (MESMAS CONEXÕES MAS SEM O OVER DO ROST):
      const orangeSmile = document.querySelector('.blob-orange .smile');
      const purpleOval = document.querySelector('.blob-purple .smile');

      const winMapX = window.innerWidth / 2;
      const winMapY = window.innerHeight / 2;

      const ratX = (e.clientX - winMapX) / winMapX;
      const ratY = (e.clientY - winMapY) / winMapY;

      const leftPanel = document.querySelector('.left-panel');
      if (leftPanel) {
        leftPanel.style.setProperty('--mouse-x', ratX);
        leftPanel.style.setProperty('--mouse-y', ratY);
      }

      if(orangeSmile) orangeSmile.style.transform = `translate(${ratX * 8}px, ${ratY * 6}px) rotate(-10deg)`;
      if(purpleOval) purpleOval.style.transform = `translate(${ratX * 4}px, ${ratY * 3}px)`;
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, [nonce]);

  // Error animation logic
  useEffect(() => {
    if (error && cardRef.current) {
      cardRef.current.classList.add("error-active");
      const timer = setTimeout(() => {
        if (cardRef.current) cardRef.current.classList.remove("error-active");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleEmailFocus = () => {
    if (cardRef.current) {
      cardRef.current.classList.add("email-active");
    }
  };

  const handleEmailBlur = () => {
    if (cardRef.current) {
      cardRef.current.classList.remove("email-active");
    }
  };

  const handlePasswordFocus = () => {
    if (cardRef.current) {
      cardRef.current.classList.remove("error-active");
      
      if (showPassword) {
        cardRef.current.classList.add("peeking-active");
        cardRef.current.classList.remove("password-active");
      } else {
        cardRef.current.classList.add("password-active");
        cardRef.current.classList.remove("peeking-active");
      }
      
      const pupils = document.querySelectorAll(".pupil");
      pupils.forEach(
        (pupil) => (pupil.style.transform = `translate(0px, 0px)`)
      );

      const orangeSmile = document.querySelector('.blob-orange .smile');
      const yellowBeak = document.querySelector('.yellow-mouth');
      const purpleOval = document.querySelector('.blob-purple .smile');
      if(orangeSmile) orangeSmile.style.transform = '';
      if(yellowBeak) yellowBeak.style.transform = '';
      if(purpleOval) purpleOval.style.transform = '';
    }
  };

  const handlePasswordBlur = () => {
    if (cardRef.current) {
      cardRef.current.classList.remove("password-active");
      cardRef.current.classList.remove("peeking-active");
    }
  };

  // Toggle senha agora atualiza o foco/estado dos monstros na hora
  const togglePasswordView = () => {
    const isNowShowing = !showPassword;
    setShowPassword(isNowShowing);
    
    // Se o input de senha ainda está focado quando trocamos a view:
    const passwordInput = document.getElementById("password");
    const signupPasswordInput = document.getElementById(`signupPwd_${nonce}`);
    
    if (document.activeElement === passwordInput || document.activeElement === signupPasswordInput) {
      if (isNowShowing) {
        cardRef.current.classList.remove("password-active");
        cardRef.current.classList.add("peeking-active");
      } else {
        cardRef.current.classList.add("password-active");
        cardRef.current.classList.remove("peeking-active");
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{appName || "OmniPlay"}</title>
        <link rel="icon" href={appLogoFavicon || "/favicon.ico"} />
      </Helmet>

      <div className="login-page-body">
        {/* ♿ Skip-link: visualmente escondido, aparece ao focar via teclado (Tab).
            Permite pular o alternador de tema e ir direto ao formulário de login. */}
        <a href="#main-login-content" className="login-skip-to-content">
          Pular para o formulário
        </a>
        <div className="card" id="card" ref={cardRef}>
          
          {/* LEFT PANEL */}
          <div className={`left-panel ${blobsLoaded ? "blobs-visible" : ""}`} id="leftPanel">
            <div className="blob blob-purple">
                <div className="face">
                    <div className="eyes">
                        <div className="eye"><div className="pupil"></div></div>
                        <div className="eye"><div className="pupil"></div></div>
                    </div>
                    <div className="smile parallax-element"></div>
                </div>
            </div>
            
            <div className="blob blob-pink">
                <div className="face">
                    <div className="eyes">
                        <div className="eye"><div className="pupil"></div></div>
                        <div className="eye"><div className="pupil"></div></div>
                    </div>
                    <div className="smile parallax-element"></div>
                </div>
            </div>

            <div className="blob blob-yellow">
                <div className="face">
                    <div className="eyes">
                        <div className="eye"><div className="pupil"></div></div> 
                    </div>
                    <div className="yellow-mouth parallax-element"></div>
                </div>
            </div>

            <div className="blob blob-orange">
                <div className="face">
                    <div className="eyes">
                        <div className="eye"><div className="pupil"></div></div>
                        <div className="eye"><div className="pupil"></div></div>
                    </div>
                    <div className="smile parallax-element"></div>
                </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="right-panel" id="main-login-content" tabIndex={-1}>
            <button
              className="login-theme-toggle-inline"
              onClick={handleToggleMode}
              aria-label="Alternar tema"
              title="Alternar tema"
            >
              {mode === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>


            {!isSignupMode ? (
              /* ========== LOGIN ========== */
              <>
                <h1>Login Omniplay</h1>

                {error && <div className="login-error">{error}</div>}

                <form onSubmit={handleSubmitLogin} autoComplete="on">
                  <div className="field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={user.email}
                      onChange={handleChangeInput}
                      onFocus={handleEmailFocus}
                      onBlur={handleEmailBlur}
                      required
                      autoComplete="username"
                      autoFocus
                    />
                  </div>

                  <div className="field">
                    <label>Senha</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={user.password}
                      onChange={handleChangeInput}
                      onFocus={handlePasswordFocus}
                      onBlur={handlePasswordBlur}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      className="eye-btn"
                      type="button"
                      onClick={togglePasswordView}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>

                  <div className="options-row">
                    <label className="remember">
                      <input 
                        type="checkbox" 
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      /> Lembrar-me
                    </label>
                  </div>

                  <button className="btn-login" type="submit" disabled={loading}>
                    {loading ? "Entrando..." : "Entrar"}
                  </button>
                </form>

                {allowSignup && (
                  <p className="signup-text">
                    Não tem uma conta?{" "}
                    <a
                      href="/pricing"
                      onClick={(e) => {
                        e.preventDefault();
                        history.push("/pricing");
                      }}
                    >
                      Criar conta
                    </a>
                  </p>
                )}
              </>
            ) : (
              /* ========== CADASTRO ========== */
              <>
                <h1>Criar conta</h1>

                {error && <div className="login-error">{error}</div>}

                <form onSubmit={handleSignUp} autoComplete="off">
                  <input type="text" name="fakeuser" autoComplete="username" style={{ display: 'none' }} />
                  <input type="password" name="fakepass" autoComplete="current-password" style={{ display: 'none' }} />

                  <div className="field">
                    <label>Nome da empresa</label>
                    <input
                      type="text"
                      name="companyName"
                      value={signupData.companyName}
                      onChange={handleSignupChange}
                      required
                    />
                  </div>

                  <div className="field">
                    <label>Seu nome</label>
                    <input
                      type="text"
                      name={`fullName_${nonce}`}
                      value={signupData.name}
                      onChange={handleSignupChange}
                      required
                    />
                  </div>

                  <div className="field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={signupData.email}
                      onChange={handleSignupChange}
                      onFocus={handleEmailFocus}
                      onBlur={handleEmailBlur}
                      required
                    />
                  </div>

                  <div className="field">
                    <label>Senha</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      id={`signupPwd_${nonce}`}
                      name={`signupPwd_${nonce}`}
                      value={signupData.password}
                      onChange={handleSignupChange}
                      onFocus={handlePasswordFocus}
                      onBlur={handlePasswordBlur}
                      required
                      autoComplete="off"
                    />
                    <button
                      className="eye-btn"
                      type="button"
                      onClick={togglePasswordView}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>

                  <div className="field">
                    <label>WhatsApp</label>
                    <div style={{ transform: 'translateY(-6px)' }}>
                      <LoginPhoneInput
                        value={signupData.phone}
                        onChange={(value) => setSignupData({ ...signupData, phone: value })}
                        placeholder="+55 11 99999-9999"
                        required
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label>Plano</label>
                    <select
                      name="planId"
                      value={signupData.planId}
                      onChange={handleSignupChange}
                      required
                    >
                      <option value="">Selecione um plano</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} - R$ {plan.amount}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button className="btn-login" type="submit" disabled={loading} style={{ marginTop: '10px' }}>
                    {loading ? "Criando..." : "Criar conta"}
                  </button>
                </form>

                <p className="signup-text">
                  Já tem uma conta?{" "}
                  <a
                    href="/login"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsSignupMode(false);
                    }}
                  >
                    Entrar
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
